import axios from 'axios'
import moment from 'moment'
import {readOsiBreakTimeMinutes} from '../appointment-management/components/vosDurationUtils'
import {
  resolveAppointmentChargeRateHours,
  resolveOsiBillingFlags,
} from '../appointment-management/components/vosFormatters'
import {formatBulkVosCallId} from './bulkVosClientPortalFormat'
import type {BatchItem} from './bulkVosTypes'

const API_URL = process.env.REACT_APP_API_URL

export function mapOsiExpensesFromApi(osiExpensesList: any[] | undefined | null) {
  if (!osiExpensesList?.length) return []
  return osiExpensesList.map((item) => ({
    description: item.description,
    amount: item.amount,
    file: item.fileName,
    dataUrl: item.fileName,
    code: item.code,
    isExpensesPayableForInterpreter: item.isExpensesPayableForInterpreter ?? true,
    isExpensesChargeableForClient: item.isExpensesChargeableForClient ?? true,
  }))
}

export function resolveBulkVosAppointmentCode(item: BatchItem): number {
  const fromDto = item.appointmentCode
  if (fromDto != null && Number.isInteger(fromDto) && fromDto > 0) return fromDto
  const fromCall = item.callId ? Number(formatBulkVosCallId(item.callId)) : NaN
  if (Number.isInteger(fromCall) && fromCall > 0) return fromCall
  return NaN
}

function parseTimeForOsiForm(timeStr: string, appointmentDay: string): {time: string; dateTime: string} | null {
  const trimmed = timeStr.trim()
  if (!trimmed) return null
  const parsed = moment(trimmed, ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD HH:mm:ss', 'HH:mm:ss', 'HH:mm', 'hh:mm A'], true)
  if (!parsed.isValid()) return null
  const time = parsed.format('HH:mm')
  const day = parsed.year() > 1970 && trimmed.includes('-') ? parsed.format('YYYY-MM-DD') : appointmentDay
  return {time, dateTime: `${day} ${time}`}
}

function resolveMeetingSummaryClientTimes(meetingSummary: any, appointmentDay: string) {
  if (!meetingSummary) return null
  const startRaw =
    meetingSummary.reqStartDate ??
    meetingSummary.ReqStartDate ??
    meetingSummary.requesterStartDate ??
    meetingSummary.RequesterStartDate ??
    meetingSummary.clientSubmittedStartDate ??
    meetingSummary.ClientSubmittedStartDate
  const endRaw =
    meetingSummary.reqEndDate ??
    meetingSummary.ReqEndDate ??
    meetingSummary.requesterEndDate ??
    meetingSummary.RequesterEndDate ??
    meetingSummary.clientSubmittedEndDate ??
    meetingSummary.ClientSubmittedEndDate
  if (!startRaw || !endRaw) return null
  const start = parseTimeForOsiForm(String(startRaw), appointmentDay)
  const end = parseTimeForOsiForm(String(endRaw), appointmentDay)
  if (!start || !end) return null
  const mileage =
    meetingSummary.requesterMilage ??
    meetingSummary.RequesterMilage ??
    meetingSummary.clientSubmittedMilage ??
    meetingSummary.ClientSubmittedMilage
  const travelH =
    meetingSummary.requesterTravelTimeH ??
    meetingSummary.RequesterTravelTimeH ??
    meetingSummary.clientSubmittedTravelTimeH ??
    meetingSummary.ClientSubmittedTravelTimeH
  const travelM =
    meetingSummary.requesterTravelTimeM ??
    meetingSummary.RequesterTravelTimeM ??
    meetingSummary.clientSubmittedTravelTimeM ??
    meetingSummary.ClientSubmittedTravelTimeM
  return {
    startTime: start.time,
    endTime: end.time,
    startDateTime: start.dateTime,
    endDateTime: end.dateTime,
    mileage,
    travelH,
    travelM,
  }
}

function resolveBatchItemClientDisputeTimes(batchItem: BatchItem | null | undefined, appointmentDay: string) {
  if (!batchItem) return null
  const startStr =
    batchItem.adjust?.editableStartTime ??
    batchItem.adjust?.originalStartTime ??
    (batchItem.isDisputed ? batchItem.startTime : undefined)
  const endStr =
    batchItem.adjust?.editableEndTime ??
    batchItem.adjust?.originalEndTime ??
    (batchItem.isDisputed ? batchItem.endTime : undefined)
  if (!startStr?.trim() || !endStr?.trim()) return null
  const start = parseTimeForOsiForm(startStr, appointmentDay)
  const end = parseTimeForOsiForm(endStr, appointmentDay)
  if (!start || !end) return null
  return {
    startTime: start.time,
    endTime: end.time,
    startDateTime: start.dateTime,
    endDateTime: end.dateTime,
    mileage: batchItem.adjust?.editableMilage ?? batchItem.adjust?.originalMilage,
    travelH: batchItem.adjust?.editableTravelTimeH ?? batchItem.adjust?.originalTravelTimeH,
    travelM: batchItem.adjust?.editableTravelTimeM ?? batchItem.adjust?.originalTravelTimeM,
  }
}

/** Bulk VOS finance: default editable times to client-disputed values for digital VOS. */
export function applyDigitalVosClientDefaultsToInitial(
  initial: Record<string, unknown>,
  meetingSummary: any,
  batchItem: BatchItem | null | undefined,
  appointmentDetails: any,
) {
  const mode = Number(
    initial.vosSubmissionMode ?? meetingSummary?.vosSubmissionMode ?? meetingSummary?.VosSubmissionMode,
  )
  if (mode !== 1) return initial

  const appointmentDay = moment(
    appointmentDetails?.startTime ?? appointmentDetails?.StartTime ?? meetingSummary?.startDate,
  ).format('YYYY-MM-DD')

  const clientTimes =
    resolveBatchItemClientDisputeTimes(batchItem, appointmentDay) ??
    resolveMeetingSummaryClientTimes(meetingSummary, appointmentDay)
  if (!clientTimes) return initial

  return {
    ...initial,
    startTime: clientTimes.startTime,
    endTime: clientTimes.endTime,
    startDateTime: clientTimes.startDateTime,
    endDateTime: clientTimes.endDateTime,
    ...(clientTimes.mileage != null && clientTimes.mileage !== ''
      ? {milageOfDrive: clientTimes.mileage}
      : {}),
    ...(clientTimes.travelH != null ? {travelHours: clientTimes.travelH} : {}),
    ...(clientTimes.travelM != null ? {travelMinutes: clientTimes.travelM} : {}),
  }
}

export function buildInitialValuesOSIFromApi(meetingSummary: any, appointmentDetails?: any) {
  const startDateTime = meetingSummary?.startDate
    ? moment(meetingSummary.startDate).format('YYYY-MM-DD HH:mm')
    : appointmentDetails?.startTime
      ? moment(appointmentDetails.startTime).format('YYYY-MM-DD HH:mm')
      : ''
  const endDateTime = meetingSummary?.endDate
    ? moment(meetingSummary.endDate).format('YYYY-MM-DD HH:mm')
    : appointmentDetails?.endTime
      ? moment(appointmentDetails.endTime).format('YYYY-MM-DD HH:mm')
      : ''

  return {
    code: meetingSummary?.code ?? '',
    startDateTime,
    endDateTime,
    startTime: meetingSummary?.startDate
      ? moment(meetingSummary.startDate).format('HH:mm')
      : appointmentDetails?.startTime
        ? moment(appointmentDetails.startTime).format('HH:mm')
        : '',
    endTime: meetingSummary?.endDate
      ? moment(meetingSummary.endDate).format('HH:mm')
      : appointmentDetails?.endTime
        ? moment(appointmentDetails.endTime).format('HH:mm')
        : '',
    travelHours: meetingSummary?.travalTimeH ?? 0,
    travelMinutes: meetingSummary?.travalTimeM ?? 0,
    isRoundTrip: meetingSummary?.isRoundTrip ?? false,
    milageOfDrive: meetingSummary?.milage ?? '',
    ...resolveOsiBillingFlags(meetingSummary),
    addStaffMember: !!(meetingSummary?.staffMemberEmail || meetingSummary?.staffMemberName),
    staffMemberName: meetingSummary?.staffMemberName ?? '',
    staffMemberEmail: meetingSummary?.staffMemberEmail ?? '',
    breakTimeMinutes: (() => {
      const value = readOsiBreakTimeMinutes(meetingSummary as Record<string, unknown>)
      return value != null ? value : ''
    })(),
    vosSubmissionMode:
      meetingSummary?.vosSubmissionMode !== undefined && meetingSummary?.vosSubmissionMode !== null
        ? Number(meetingSummary.vosSubmissionMode)
        : undefined,
  }
}

export async function fetchOsiEditContext(appointmentCode: number) {
  const [appointmentRes, meetingSummaryRes, interpretersRes] = await Promise.all([
    axios.get(`${API_URL}/Appoinment/${appointmentCode}`),
    axios.get(`${API_URL}/Appoinment/osi-submission-appointment/${appointmentCode}`),
    axios.get(`${API_URL}/Appoinment/details-with-interpreters/${appointmentCode}`),
  ])
  const appointmentDetails = appointmentRes.data
  const meetingSummary = meetingSummaryRes.data
  const interpreters =
    interpretersRes.data?.availableInterpreters || interpretersRes.data?.interpreters || []
  return {
    appointmentDetails,
    meetingSummary,
    initialValuesOSI: {
      ...buildInitialValuesOSIFromApi(meetingSummary, appointmentDetails),
      ...resolveAppointmentChargeRateHours(interpreters),
    },
    expenseList: mapOsiExpensesFromApi(meetingSummary?.osiExpensesList),
  }
}
